import React, { useContext, useState, useEffect, useCallback } from 'react'
import { AuthContext } from '@/layout/Auth'
import { InfoCard } from '../components'
import { OrgIcon, Content } from './components'
import Text from '@/components/Text'
import EditableInfo from './infoItem'
import { useMutation, useQuery } from '@tanstack/react-query'
import { updateOrgInfo, getOrgInfo, dissolveOrg, leaveOrg } from '@/api'
import Button from '@/components/Button'
import { Stack, TextField } from '@mui/material'
import { dangerHover, errorText } from '@/constant'
import Modal, { confirm } from '@/components/Modal'
import { ModalContent } from '@/components/Modal/comfirmation'
import ClearIcon from '@mui/icons-material/Clear'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/hooks'
import { primary } from '@/constant'

const Info: React.FC<unknown> = () => {
  const navigation = useNavigate()
  const perms = usePermission()
  const isAdmin = perms?.includes('admin')
  const { authInfo, updateAuthInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [open, setOpen] = useState<boolean>(false)
  const [confirmOrgName, setConfirmOrgName] = useState<string>('')
  const [queryInfo, setQueryInfo] = useState<boolean>(false)
  // @ts-ignore
  useQuery(['getOrgInfo', orgInfo?.id], () => getOrgInfo(orgInfo?.id), {
    onError: (e: string) => {
      setQueryInfo(false)
    },
    onSuccess: (data) => {
      const newOrg = { ...orgInfo, ...data }
      updateAuthInfo({ orgInfo: newOrg })
      setQueryInfo(false)
    },
    enabled: queryInfo,
  })

  const orgInfoMutation = useMutation(updateOrgInfo, {
    onSuccess: () => {
      setQueryInfo(true)
    },
  })

  const leaveOrgMutation = useMutation(leaveOrg, {
    onSuccess: () => {
      navigation(0)
    },
    onError: (err: string | number) => {
      if (err === 4123) {
        confirm({
          width: 480,
          title: '提示',
          content: (
            <Text>
              由于您是空间内唯一管理员，须前往
              <a style={{ color: primary }} href='/console/space/base'>
                用户管理
              </a>
              设置一位用户成为管理员后，再次操作退出空间
            </Text>
          ),
          hideClose: true,
          closeAfterClose: true,
          // @ts-ignore
          onConfirm: null,
          // @ts-ignore
          onClose: null,
        })
      }
    },
  })

  const dissolveOrgMutation = useMutation(dissolveOrg, {
    onSuccess: () => {
      navigation('/console/workbench')
      navigation(0)
    },
  })

  const handleUpdateName = useCallback(
    (name: string) => {
      // @ts-ignore
      const updated = { name, description: orgInfo.description }
      // @ts-ignore
      orgInfoMutation.mutate({
        // @ts-ignore
        id: orgInfo.id,
        info: { ...updated },
      })
    },
    [orgInfo?.description, orgInfo?.id, orgInfoMutation],
  )

  const handleUpdateDesc = useCallback(
    (desc: string) => {
      // @ts-ignore
      const updated = { name: orgInfo.name, description: desc }
      orgInfoMutation.mutate({
        // @ts-ignore
        id: orgInfo.id,
        info: { ...updated },
      })
    },
    [orgInfo?.id, orgInfo?.name, orgInfoMutation],
  )

  const EndOrg = useCallback(() => {
    if (orgInfo?.id) dissolveOrgMutation.mutate(orgInfo?.id)
  }, [dissolveOrgMutation, orgInfo?.id])

  const handleEndOrg = useCallback(() => {
    setOpen(true)
  }, [])

  const handleCloseEndOrg = useCallback(() => {
    setOpen(false)
  }, [])

  const handleChangeConfirmOrgName = useCallback((event: React.ChangeEvent) => {
    const target = event.target as HTMLInputElement
    const input = target?.value?.trim()
    setConfirmOrgName(input ?? '')
  }, [])

  const handleExitOrg = useCallback(
    () => () => {
      confirm({
        onConfirm: () => {
          // @ts-ignore
          leaveOrgMutation.mutate(orgInfo?.id)
        },
        // @ts-ignore
        onClose: null,
        title: '提示',
        content: (
          <Stack sx={{ gap: '8px' }}>
            <Text>{`确认退出空间：${orgInfo?.name}？`}</Text>
            <Text size='xs' sx={{ opacity: 0.4 }}>
              退出空间后，您在该空间所产生的数据将不可见，包括不限于：已开通或已购买但未到期的产品应用，站内消息，消息通知策略等。
            </Text>
          </Stack>
        ),
        closeAfterClose: true,
      })
    },
    [leaveOrgMutation, orgInfo?.id, orgInfo?.name],
  )

  useEffect(() => {
    if (orgInfo?.id) {
      setQueryInfo(true)
    }
  }, [orgInfo?.id])

  return (
    <InfoCard>
      <Content>
        <OrgIcon>{orgInfo?.name?.[0]?.toUpperCase()}</OrgIcon>
        <Text size='lg' sx={{ maxWidth: '320px' }}>
          {orgInfo?.name}
        </Text>
        <EditableInfo
          key='orgInfo-id'
          editable={false}
          label={'空间 ID'}
          // @ts-ignore
          value={orgInfo?.id}
        />
        <EditableInfo
          key='orgInfo-name'
          editable={isAdmin}
          label={'空间名称'}
          // @ts-ignore
          value={orgInfo?.name}
          // @ts-ignore
          onSave={handleUpdateName}
        />
        <EditableInfo
          key='orgInfo-desc'
          editable={isAdmin}
          label={'空间描述'}
          multiLine={true}
          // @ts-ignore
          value={orgInfo?.description}
          // @ts-ignore
          onSave={handleUpdateDesc}
        />
      </Content>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'flex-start',
          gap: '16px',
          paddingLeft: '30px',
          paddingTop: '30px',
        }}
      >
        {isAdmin && (
          <Button onClick={handleEndOrg} type='danger' sx={{ color: '#FFF', background: dangerHover }}>
            解散空间
          </Button>
        )}
        <Button
          onClick={handleExitOrg()}
          size='small'
          type={'cancel'}
          sx={{
            color: errorText,
            backgroundColor: '#F7F7F7',
            '&:hover': { color: '#FF8F85', backgroundColor: '#F7F7F7' },
          }}
        >
          退出空间
        </Button>
        <Modal open={open} width={500} onClose={handleCloseEndOrg}>
          <ModalContent sx={{ gap: '8px' }}>
            <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text weight='bold'>提示</Text>
              <ClearIcon sx={{ cursor: 'pointer' }} onClick={handleCloseEndOrg} />
            </Stack>
            <Text weight='regular'>确认解散此空间？</Text>
            <Text size='xs' weight='regular' sx={{ opacity: 0.4 }}>
              解散空间后，该空间所有数据将被一并删除，包括不限于：空间成员，已开通或已购买但未到期的产品应用，站内消息，消息通知策略等。
            </Text>
            <TextField
              placeholder={'请确认要解散的空间名称'}
              value={confirmOrgName}
              onChange={handleChangeConfirmOrgName}
            />
            <Stack
              sx={{
                flexDirection: 'row',
                marginTop: '8px',
                justifyContent: 'flex-end',
              }}
            >
              <Button type='text' onClick={handleCloseEndOrg}>
                取消
              </Button>
              <Button
                type='highlight'
                onClick={EndOrg}
                disabled={confirmOrgName !== orgInfo?.name}
                sx={{ boxShadow: 'none' }}
              >
                确定
              </Button>
            </Stack>
          </ModalContent>
        </Modal>
      </Stack>
    </InfoCard>
  )
}

export default Info
